import { useMemo } from 'react';
import { isNil } from '../utils/fp-utils.js';
import { buildItemKey } from '../../../app/cart/persist.js';

/**
 * Joins the three sources that describe what's in the cart into something a
 * component can render directly:
 *
 *   - `lineItems`  — what the customer chose (product code + quantity, from the cookie)
 *   - `pricing`    — what it costs (from the pricing endpoint)
 *   - `catalog`    — what it's called and what it looks like (title, description, image)
 *
 * This join used to sit inline in the price display component, alongside a standing TODO
 * observing that it shouldn't need three sources. It still needs three — that's a
 * shape imposed by the APIs, not something a refactor here can fix — but the
 * component no longer has to know about it.
 *
 * The summary figures are returned as data rather than being hand-written as four
 * near-identical JSX blocks, so the markup becomes a map.
 */
const useCartDisplayItems = ({ catalog, pricing, lineItems }) => useMemo(() => {
    const {
        adjustedSubtotal,
        currencyType,
        currencySymbol,
        lineItems: pricingLineItems,
        taxInfo,
        total,
    } = pricing ?? {};

    // Pricing arrives after the cart contents do, so until a subtotal exists there
    // is nothing meaningful to show per row. Callers render a '-' placeholder.
    const hasValidPricing = !isNil(adjustedSubtotal);

    const displayLineItems = hasValidPricing ?
        lineItems?.map((item) => {
            const pricingDetails = pricingLineItems?.find((lineItem) => lineItem.productCode === item.productCode);
            const productDetails = catalog?.products?.find((product) => product.productCode === item.productCode);
            const subscriptionDetails = catalog?.subscriptions?.find(subscription => subscription.productCode === item.productCode && subscription.currency === currencyType);

            return {
                key: buildItemKey(item.productCode, item.selection),
                product: {
                    productCode: item.productCode,
                    title: productDetails?.title,
                    description: productDetails?.description,
                    attributes: productDetails?.attributes, // For attribute names?
                    tags: productDetails?.tags,
                    imageUrl: productDetails?.imageUrl,
                },
                item: {
                    quantity: item.quantity,
                    selection: item.selection,
                    unitPrice: pricingDetails?.unitPrice,
                    subtotal: pricingDetails?.baseSubtotal ?? pricingDetails?.adjustedSubtotal,
                    currencyCode: currencyType,
                    currencySymbol,

                    discounts: pricingDetails?.discounts, // TODO: ?
                },
                ...Boolean(subscriptionDetails) && {
                    subscription: {
                        required: true,
                        proratedAmount: subscriptionDetails?.proratedAmount,
                        unitPrice: subscriptionDetails?.baseUnitPrice,
                        interval: subscriptionDetails?.interval,
                        discount: {
                            threshold: subscriptionDetails?.discountThreshold,
                            unitPrice: subscriptionDetails?.adjustedUnitPrice,
                        },
                    },
                },
            };
        })?.sort((a, b) => a.key.localeCompare(b.key)) ?? [] :
        [];

    const summaryCallouts = [];
    const subscriptionSummary = displayLineItems.reduce((acc, lineItem) => {
        if (lineItem.subscription) {
            acc.proratedAmount = (acc.proratedAmount || 0) + lineItem.subscription.proratedAmount;
            acc.billingAmount = (acc.billingAmount || 0) + lineItem.subscription.unitPrice * lineItem.item.quantity;
            acc.interval = lineItem.subscription.interval; // TODO: assumes same billing interval for all
            if (!acc.breakdowns) {
                acc.breakdowns = [];
            }

            if (lineItem.item.quantity >= lineItem.subscription.discount.threshold) {
                const savingAmount = lineItem.item.quantity * lineItem.subscription.unitPrice - lineItem.item.quantity * lineItem.subscription.discount.unitPrice;

                acc.breakdowns.push({
                    unitPrice: lineItem.subscription.discount.unitPrice,
                    quantity: lineItem.item.quantity,
                });

                summaryCallouts.push({
                    productCode: lineItem.product.productCode, // Needed to identify the callout
                    threshold: lineItem.subscription.discount.threshold,
                    discountPrice: lineItem.subscription.discount.unitPrice,
                    savingAmount,
                    interval: lineItem.subscription.interval,
                });
            } else {
                acc.breakdowns.push({
                    unitPrice: lineItem.subscription.unitPrice,
                    quantity: lineItem.item.quantity,
                });
            }
        }
        return acc;
    }, {});

    const summary = {
        callouts: summaryCallouts,
        currencyCode: currencyType,
        currencySymbol,
        shipping: 0,
        ...Object.keys(subscriptionSummary).length > 0 ? { subscriptions: subscriptionSummary } : {},
        subtotal: adjustedSubtotal,
        taxes: taxInfo?.amount,
        total,
    };

    return {
        displayLineItems,
        hasValidPricing,
        summary,
    };
}, [catalog, pricing, lineItems]);

export default useCartDisplayItems;
